import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import styled from "styled-components";
import { DateDisplay, ProfileDisplay } from "../../../components";
import { StandardTableSortableHeader } from "../../../components/tables/StandardTable";
import { ThemedSpan } from "../../../components/utilities";
import { useFilterContext } from "../../../hooks/FilterContext";
import { useAddClientToFavouritesMutation, useRemoveClientFromFavouritesMutation } from "../../../services/clientdetails";
import { useCreateSetFromClientsMutation, useLazyFetchMasterAccountsQuery, useLazySearchMasterAccountsQuery } from "../../../services/clientsearch";
import { usePatchMasterAccountDetailsMutation } from "../../../services/clientdetails";
import { openClientVulnerabilityModal } from "../../../components/modals/client-factfind-vulnerability";
import useOpenMasterAccount from "../../../hooks/useOpenMasterAccount";
import { toNString } from "../../../helpers/guids";
import { Dropdown } from "react-bootstrap";
import React from "react";
import { createPortal } from "react-dom";

// Helper functions to format cells
const formatCurrency = (value) => value?.toLocaleString("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 })
const formatUser = (value) => <ProfileDisplay userId={value} />;

const ClientManagementNavigationCell = ({ value, row, destination = null, getDestination }) => {
    return <a href={destination ?? getDestination(row)}>{value}</a>
}

const ClientManagementSplitCell = ({ value, row, cellFormatter, dateProperty, nullText = "N/A", destination = null, getDestination }) => {
    const date = useMemo(() => row[dateProperty]
        ? <DateDisplay format="Do MMM 'YY">{row[dateProperty]}</DateDisplay>
        : null, [row, dateProperty]);

    return <p className="m-0 lh-1 text-truncate">
        <a href={destination ?? getDestination(row)}>
            {value == null
                ? nullText
                : cellFormatter && typeof cellFormatter === 'function'
                    ? cellFormatter(value)
                    : value}
        </a>
        <br />
        <ThemedSpan variant="muted" className="fs-smallest">
            {date}
        </ThemedSpan>
    </p>;
}

const RowIconWrapper = styled.div`
    margin-left: auto;
    margin-right: auto;
    width: min-content;
`

const ClientManagementBooleanIconCell = ({
    value,
    row,
    onIconClick,
    getTippyLabel = (value) => value ? "Add" : "Remove",
    getIcon,
    getColour,
    property
}) => {
    const { masterAccountId } = row;
    let isClickable;
    if (onIconClick && typeof onIconClick === 'function') {
        if (property === "isVulnerable" && !value) {
            isClickable = false
        }
        else {
            isClickable = true
        }
    }
    const handleClick = () => onIconClick(masterAccountId, !value);

    const icon = <RowIconWrapper>
        <FontAwesomeIcon
            className={classNames({ "has-pointer": isClickable })}
            onClick={isClickable ? handleClick : null}
            icon={getIcon(value)}
            color={getColour(value)}
        />
    </RowIconWrapper>

    if (isClickable)
        return <Tippy placement="left" content={(
            <span>{getTippyLabel(value)}</span>
        )}>
            {icon}
        </Tippy>

    return icon;
}

const ActionColumnParent = styled.td`
    width: ${({ width }) => width}%;
    cursor: pointer;
    vertical-align: middle;
    margin-left: auto;
    margin-right: auto;
`;

const ActionColumnParentToggle = React.forwardRef(({ value, getIcon, getColour, ...rest },  ref) => (
    <div ref={ref} {...rest}>
        <FontAwesomeIcon 
            icon={getIcon(value)}
            color={getColour(value)} 
        />
    </div>
));

const ClientManagementCustodianMatchingCell = ({
    value,
    row,
    onIconClick,
    getTippyLabel = (value) => value ? "Add" : "Remove",
    getIcon,
    getColour,
    property
}) => {
    const { masterAccountId } = row;
    const menuId = React.useId();
    // let isClickable;
    // if (onIconClick && typeof onIconClick === 'function') {
    //     if (value === 2 || value == null) {
    //         isClickable = false
    //     }
    //     else {
    //         isClickable = true
    //     }
    // }
    
    const handleClick = (newValue) => onIconClick(masterAccountId, newValue);

    // if(!isClickable){
    //     return <RowIconWrapper>
    //         <FontAwesomeIcon
    //             className={classNames({ "has-pointer": isClickable })}
    //             icon={getIcon(value)}
    //             color={getColour(value)}
    //         />
    //     </RowIconWrapper>
    // }

    return <Tippy placement="left" content={(
                <span>{getTippyLabel(value)}</span>
            )}>
                <ActionColumnParent width={5}>
                    <Dropdown className="caret-off">
                        <Dropdown.Toggle id={menuId} as={ActionColumnParentToggle} value={value} getIcon={getIcon} getColour={getColour} />
                        
                        {createPortal(
                            <Dropdown.Menu style={{ maxWidth: '14rem' }}>
                                <Dropdown.Header>
                                    Custodian Matching Status
                                </Dropdown.Header>
                                <Dropdown.Item onClick={() => handleClick(0)}>
                                    <span className="ms-1">Match</span>
                                    {value === 0 && <FontAwesomeIcon
                                                    icon="check"
                                                    className="text-success ms-2"
                                                />}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleClick(1)}>
                                    <span className="ms-1">Ignore</span>
                                    {value === 1 && <FontAwesomeIcon
                                                    icon="check"
                                                    className="text-success ms-2"
                                                />}
                                </Dropdown.Item>
                                <Dropdown.Item disabled>
                                    <span className="ms-1">Not In Set</span>
                                    {(value === 2 || value === null) && <FontAwesomeIcon
                                                    icon="check"
                                                    className="text-success ms-2"
                                                />}
                                </Dropdown.Item>
                        </Dropdown.Menu>, 
                        document.body
                        )}
                    </Dropdown>
                </ActionColumnParent>
    </Tippy>
}


const limit = 50;
const defaultResult = { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };

const useClientManagement = ({ searchTerm }) => {
    const { filter, isReady } = useFilterContext();

    // Search/fetch handling inc. sorting, and pagination. Fetch is sortable, search sorts by score in backend
    const [searchCacheKey, setSearchCacheKey] = useState(new Date().valueOf());
    const [fetchCacheKey, setFetchCacheKey] = useState(new Date().valueOf());

    const [fetchClients, { currentData: fetchedClients, isFetching, isError: fetchIsError, error: fetchError }] = useLazyFetchMasterAccountsQuery();
    const [searchClients, { currentData: searchedClients, isFetching: isSearching, isError: searchIsError, error: searchError }, { lastArg }] = useLazySearchMasterAccountsQuery();
    // Need a custom bit of logic to handle loading when switching between search and fetch
    // We want to hide the old results when typing in the search bar
    const [lastQueryIsFetch, setLastQueryIsFetch] = useState(null);
    const isLoadingFetch = useMemo(() => !lastQueryIsFetch && isFetching, [isFetching, lastQueryIsFetch]);
    const isLoadingSearch = useMemo(() => (lastQueryIsFetch || lastArg.searchTerm !== searchTerm) && isSearching, [isSearching, lastArg.searchTerm, lastQueryIsFetch, searchTerm])

    const isError = fetchIsError || searchIsError;
    const error = fetchIsError
        ? fetchError
        : searchIsError
            ? searchError
            : null;

    // A bit of state to handle hiding the old results when typing in the search bar
    const [isTyping, setIsTyping] = useState(false);

    const { pagination, results } = (searchTerm.length >= 3
        ? searchedClients
        : fetchedClients)
        || defaultResult;
    const { totalCount, page } = pagination;
    const [sort, setSort] = useState(null);
    const [currentSortProperty, currentSortDirection] = useMemo(() => sort?.split("+") ?? [], [sort]);
    const [isClearing, setIsClearing] = useState(false);

    // If the user types while a fetch/search is in progress it will be skipped by the clear function
    // This state is used to keep track of whether a search has been skipped and to trigger a new search when the fetch/search is complete
    const [skippedSearches, setSkippedSearches] = useState(false);

    const clear = useCallback(() => new Promise((res, rej) => {
        if (isSearching)
            setSkippedSearches(true);

        setIsClearing(true)

        const newCacheKey = new Date().valueOf();

        const fetchFn = searchTerm === "" ? fetchClients : searchClients;
        const cacheKeySetter = searchTerm === "" ? setFetchCacheKey : setSearchCacheKey;

        fetchFn({ page: 1, limit, sort, searchTerm, cacheKey: newCacheKey, ...filter })
            .unwrap()
            .then(() => cacheKeySetter(newCacheKey))
            .then(() => setLastQueryIsFetch(searchTerm === ""))
            .then(res, rej)
            .finally(() => setIsClearing(false));
    }), [fetchClients, filter, isSearching, searchClients, searchTerm, sort]);

    useEffect(() => {
        if (isFetching || isSearching || !isReady) return;

        if (skippedSearches) {
            setSkippedSearches(false);
            clear();
        }
    }, [clear, isFetching, isReady, isSearching, skippedSearches])

    const isRowLoaded = (index) => index < results.length;

    const loadMore = () => {
        if (isFetching || isSearching) return;

        if (searchTerm !== "") {
            searchClients({ cacheKey: searchCacheKey, searchTerm, page: page + 1, limit, ...filter });
        } else {
            fetchClients({ cacheKey: fetchCacheKey, sort, page: page + 1, limit, ...filter });
        }
    };

    const onClickSort = useCallback((property) => {
        let newDirection = "";

        // if the current property is already selected, reverse the sort direction
        if (property === currentSortProperty) {
            newDirection = currentSortDirection === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
    }, [currentSortDirection, currentSortProperty]);

    const reload = (e) => {
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }

        clear();
    };

    // Search term useEffect
    // Search should trigger on a timeout to prevent too many requests
    useEffect(() => {
        if (!isReady)
            return;

        if ((searchTerm.length > 0 && searchTerm.length < 3) || (!lastQueryIsFetch && searchTerm.length === 0))
            return;

        setIsTyping(true);

        const searchTimeout = setTimeout(() => {
            clear().then(() =>
                setIsTyping(false));
        }, 250);

        return () => clearTimeout(searchTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // Filter/sort useEffect
    // When the filter/sort term changes, clear the results and fetch new ones (as long as the search term is not 1-2 characters)
    useEffect(() => {
        if (!isReady)
            return;

        if (searchTerm.length > 0 && searchTerm.length < 3)
            return;

        clear();

        // Disabled eslint warning because we only want to run this effect when the filter/sort term changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, filter]);

    const sortableHeaderRenderer = useCallback(({ headerClassName, label, property, key, sortProperty = property }) => {
        return <StandardTableSortableHeader
            className={headerClassName}
            key={key}
            onClick={() => onClickSort(sortProperty)}
            active={sortProperty === currentSortProperty}
            direction={currentSortDirection}
            isSticky>
            {label}
        </StandardTableSortableHeader>;
    }, [currentSortDirection, onClickSort, currentSortProperty]);

    const [addToFavourites] = useAddClientToFavouritesMutation();
    const [removeFromFavourites] = useRemoveClientFromFavouritesMutation();
    
    const handleFavouriteChanged = (masterAccountId, newValue) => {
        const promise = newValue
        ? addToFavourites({ masterAccountId }).unwrap()
        : removeFromFavourites({ masterAccountId }).unwrap()
        
        return toast.promise(promise, {
            loading: `Processing...`,
            success: `The client has been ${newValue ? "added to" : "removed from"} your favourites.`,
            error: (err) => {
                const { message } = err || { message: 'There was an unknown error.' };
                return message;
            }
        });
    }
    
    const [patch] = usePatchMasterAccountDetailsMutation();

    const handlePatchMasterAccount = (masterAccountId, value) => {
        
        let operations = [{ op: 'replace', path: `/custodianMatchingStatus`, value }];
        var promise = patch({ masterAccountId, operations}).unwrap()
        
        return toast.promise(promise, {
            loading: `Processing...`,
            success: `Custody matching status updated.`,
            error: (err) => {
                const { message } = err || { message: 'There was an unknown error.' };
                return message;
            }
        });
    }

    

    const openMasterAccount = useOpenMasterAccount();

    const onOpenClientVulnerabilityModal = (masterAccountId) => {
        openClientVulnerabilityModal(masterAccountId)
            .then(
                (proceed) => {
                    if (proceed) {
                        const sectionStorageKey = `${toNString(masterAccountId)}-fact-find-sections`;
                        const activeKeys = JSON.parse(sessionStorage.getItem(sectionStorageKey)) ?? [];
                        if (!activeKeys.includes("vulnerability")) {
                            activeKeys.push("vulnerability");
                            sessionStorage.setItem(sectionStorageKey, JSON.stringify(activeKeys));
                        }
                        openMasterAccount(masterAccountId, { suffix: "factfind" });
                    }
                }
            );
    }

    const columns = [
        {
            label: "Ref",
            property: "accountReference",
            width: 5
        },
        {
            label: "Account Name",
            property: "accountName",
            headerRenderer: sortableHeaderRenderer,
            CellComponent: ClientManagementNavigationCell,
            getDestination: ({ masterAccountId }) => `/client/${masterAccountId}`,
            width: 15
        },
        {
            label: "Account Type",
            property: "accountTypeDescription",
            sortProperty: "accountType",
            headerRenderer: sortableHeaderRenderer,
            width: 10
        },
        {
            label: "Status",
            property: "accountStatusDescription",
            sortProperty: "accountStatus",
            headerRenderer: sortableHeaderRenderer,
            width: 10
        },
        {
            label: "Adviser",
            property: "adviserId",
            headerRenderer: sortableHeaderRenderer,
            cellFormatter: formatUser,
            width: 10
        },
        {
            label: "Review Adviser",
            property: "reviewAdviserId",
            headerRenderer: sortableHeaderRenderer,
            cellFormatter: formatUser,
            width: 10
        },
        {
            label: "Address",
            property: "address",
            width: 15
        },
        {
            label: "Portfolio Value",
            property: "portfolioValue",
            headerRenderer: sortableHeaderRenderer,
            CellComponent: ClientManagementSplitCell,
            dateProperty: "portfolioValueDate",
            getDestination: ({ masterAccountId }) => `/client/${masterAccountId}/portfolio`,
            allowZero: false,
            nullText: "Never Requested",
            cellFormatter: formatCurrency,
            className: "text-end",
            headerClassName: "text-center",
            width: 7.5
        },
        {
            label: "Matching",
            property: "custodianMatchingStatus",
            headerRenderer: sortableHeaderRenderer,
            CellComponent: ClientManagementCustodianMatchingCell,
            getTippyLabel: (value) => 'Choose matching status',
            getIcon: (value) => {
                if(value === 0) return "fa-check"
                if(value === 1) return "fa-x"
                return "fa-minus";
            },
            getColour: (value) => {
                if(value === 0) return "green"
                if(value === 1) return "red"
                return "black";
            },
            onIconClick: handlePatchMasterAccount,
            className: "text-center",
            headerClassName: "text-center",
            width: 5
        },
        {
            label: "Vulnerable",
            property: "isVulnerable",
            headerRenderer: sortableHeaderRenderer,
            CellComponent: ClientManagementBooleanIconCell,
            getTippyLabel: (value) => value ? 'Click to see vulnerabilities' : null,
            onIconClick: onOpenClientVulnerabilityModal,
            getIcon: (value) => value ? "fa-flag" : ["far", "fa-flag"],
            getColour: (value) => value ? "red" : null,
            className: "text-center",
            headerClassName: "text-center",
            width: 5,
        },
        {
            label: "Favourite",
            property: "isFavourited",
            headerRenderer: sortableHeaderRenderer,
            CellComponent: ClientManagementBooleanIconCell,
            getTippyLabel: (value) => value ? 'Remove from favourites' : 'Add to favourites',
            getIcon: (value) => value ? "fa-star" : ["far", "fa-star"],
            getColour: (value) => value ? "gold" : null,
            onIconClick: handleFavouriteChanged,
            className: "text-center",
            headerClassName: "text-center",
            width: 5
        },
    ];

    const [createSetTrigger] = useCreateSetFromClientsMutation();

    const createClientSet = (setName) => {
        toast.promise(createSetTrigger({
            setName,
            searchTerm,
            ...filter,
        }).unwrap(), {
            loading: "Starting set creation...",
            success: "Set creation started. You will be notified when this is finished",
            error: (err) => {
                console.error(err);
                const { data } = err;
                if (data)
                    return data.message ?? data;

                return err?.message ?? "Could not start set creation";
            }
        });
    }

    return {
        results,
        sort,
        renderCount: totalCount === results.length ? totalCount : results.length + 3,
        totalCount,
        columns,
        isLoading: isLoadingFetch || isLoadingSearch || isTyping || isClearing,
        isClearing,
        isError,
        error,
        clear,
        isRowLoaded,
        loadMore,
        sortableHeaderRenderer,
        reload,
        createClientSet,
    };
}

export default useClientManagement;